import { css, html, nothing } from "lit";
import { ifDefined } from "lit-html/directives/if-defined.js";
import "../../atom/Icon/index.js";
import "../../atom/Spacer/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { getInputClasses, getLabelTextClasses, getMessageClasses, getIconClasses, getWrapperClasses } from "./utils.js";
import { preventDefault } from "../../../decorators/preventDefault.js";
import { query, state } from "lit/decorators.js";
import { TestingAttrs } from "./input.selectors.js";
import { testingAttr } from "../../../directives/testingAttr.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let DsfInput = class extends BaseComponent {
  constructor() {
    super(...arguments);
    this.value = "";
    this.dsf_error = false;
    this.dsf_success = false;
    this.dsf_type = "text";
    this.dsf_size = "medium";
    this.disabled = false;
    this.readonly = false;
    this.dsf_icon_right_clickable = true;
    this.dsf_icon_left_clickable = false;
    this._focused = false;
  }
  get focused() {
    return this._focused;
  }
  get hasContent() {
    return this.childElementCount > 0;
  }
  focus() {
    if (!this.disabled && !this._focused) {
      this._focused = true;
      super.focus();
      if (
        // setSelectionRange only available for these types
        ["text", "search", "tel", "password"].includes(this.dsf_type)
      ) {
        const { length } = this.inputElement.value;
        this.inputElement.setSelectionRange(length, length);
      }
    }
  }
  onInput(event) {
    this.value = event.target.value;
    this.emit({ name: "input", payload: this.value });
  }
  onBlur() {
    this._focused = false;
  }
  onFocus() {
    this.focus();
  }
  onIconRightClick() {
    if (!this.disabled && this.dsf_icon_right_clickable) {
      this.emit("icon-right-click");
    }
  }
  onIconLeftClick() {
    if (!this.disabled && this.dsf_icon_left_clickable) {
      this.emit("icon-left-click");
    }
  }
  onInputClick() {
    this.focus();
  }
  render() {
    const inputClass = getInputClasses(this);
    const labelTextClass = getLabelTextClasses(this);
    const label = this.dsf_label ? html`<label id="label" data-skeleton class="${labelTextClass}">${this.dsf_label}</label>` : nothing;
    const messageClass = getMessageClasses(this);
    const message = this.dsf_message ? html`<span class="${messageClass}" data-skeleton>${this.dsf_message}</span>` : nothing;
    const iconRight = this.dsf_icon_right ? html` <dsf-icon
          @click="${this.onIconRightClick}"
          class="${getIconClasses(this, true)}"
          dsf_name="${this.dsf_icon_right}"
          ${testingAttr(TestingAttrs.iconRight)}
        ></dsf-icon>` : nothing;
    const iconLeft = this.dsf_icon_left ? html`<dsf-icon
          @click="${this.onIconLeftClick}"
          class="${getIconClasses(this)}"
          dsf_name="${this.dsf_icon_left}"
          ${testingAttr(TestingAttrs.iconLeft)}
        ></dsf-icon>` : nothing;
    return html`
      <div class="dsf-text-s flex min-w-0 flex-auto flex-col font-sans" @click=${this.onInputClick}>
        ${label}
        <div class=${getWrapperClasses(this)} data-skeleton>
          ${iconLeft}
          <input
            aria-labelledby="label"
            ?autofocus=${this.autofocus}
            ?readonly=${this.readonly}
            name=${ifDefined(this.dsf_name)}
            type=${this.dsf_type}
            class=${inputClass}
            placeholder=${ifDefined(this.dsf_placeholder)}
            .value=${this.value}
            ?disabled="${this.disabled}"
            @input="${this.onInput}"
            @blur="${this.onBlur}"
            @focus="${this.onFocus}"
            @click="${this.onInputClick}"
            min="${ifDefined(this.dsf_min)}"
            max="${ifDefined(this.dsf_max)}"
            ${testingAttr(TestingAttrs.input)}
          />
          <slot></slot>
          ${iconRight}
        </div>
        ${message}
      </div>
    `;
  }
};
DsfInput.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        display: inline-flex;
      }

      input[type='time']::-webkit-calendar-picker-indicator {
        display: none;
      }
    `
];
__decorateClass([
  prop({ type: String })
], DsfInput.prototype, "value", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfInput.prototype, "dsf_error", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfInput.prototype, "dsf_success", 2);
__decorateClass([
  prop({
    type: String,
    reflect: true,
    validator: (value) => ["text", "password", "tel", "time", "number", "email"].includes(value)
  })
], DsfInput.prototype, "dsf_type", 2);
__decorateClass([
  prop({
    type: String,
    reflect: true,
    validator: (value) => ["medium", "small"].includes(value)
  })
], DsfInput.prototype, "dsf_size", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfInput.prototype, "disabled", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfInput.prototype, "readonly", 2);
__decorateClass([
  prop({ type: String })
], DsfInput.prototype, "dsf_placeholder", 2);
__decorateClass([
  prop({ type: String })
], DsfInput.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfInput.prototype, "dsf_label", 2);
__decorateClass([
  prop({ type: String })
], DsfInput.prototype, "dsf_name", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfInput.prototype, "dsf_icon_right", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfInput.prototype, "dsf_icon_left", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfInput.prototype, "dsf_icon_right_clickable", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfInput.prototype, "dsf_icon_left_clickable", 2);
__decorateClass([
  prop({ type: String })
], DsfInput.prototype, "autocomplete", 2);
__decorateClass([
  prop({ reflect: true })
], DsfInput.prototype, "dsf_min", 2);
__decorateClass([
  prop({ reflect: true })
], DsfInput.prototype, "dsf_max", 2);
__decorateClass([
  prop({ type: String })
], DsfInput.prototype, "dsf_icon_right_class", 2);
__decorateClass([
  query("input")
], DsfInput.prototype, "inputElement", 2);
__decorateClass([
  state()
], DsfInput.prototype, "_focused", 2);
__decorateClass([
  preventDefault
], DsfInput.prototype, "onInput", 1);
__decorateClass([
  preventDefault
], DsfInput.prototype, "onIconRightClick", 1);
__decorateClass([
  preventDefault
], DsfInput.prototype, "onIconLeftClick", 1);
DsfInput = __decorateClass([
  defineElement("dsf-input")
], DsfInput);
export {
  DsfInput
};
